// apps/vendor/src/pages/settings/PreferencesPage.tsx
//
// Phase 1 Task 49 — Preferences page (theme + language + email + channels).
// PATCH /api/v1/users/me/preferences per API-CONTRACTS L3592 +.
//
// ThemeProvider's `onThemeChange` (wired in main.tsx during Task 44) already
// calls this endpoint best-effort on every theme cycle. This page exposes the
// explicit picker UI plus the broader preferences surface (language /
// emailEnabled / notificationChannels.{inApp,email,sms}).
//
// Phase 7 Wave 18 Task 79 (Amendment #1) — fixes the language phantom-feature:
//   - Hydrates `form.language` from the live i18next locale on mount (priority
//     over `user.language`) so the picker matches whatever the TopBar
//     LanguageSelector / cookie/localStorage detector last set.
//   - Calls `i18n.changeLanguage()` BEFORE the PATCH so the UI flips
//     immediately on submit, independent of network latency.
//   - LanguageDetector cookie + localStorage auto-persist (no manual sync).

import { useEffect, useState } from 'react';
import type { FormEvent, JSX } from 'react';

import { apiClient } from '@printway/shared/api';

// Phase 7 Wave 18 Task 79 — bridge to the vendor i18next instance.
import { useTranslation } from '../../lib/i18n/index.js';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Form,
  FormControl,
  FormField,
  FormLabel,
  FormMessage,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useAuth,
  useToast,
  useTheme,
  type Theme,
  SettingsLayout,
} from '@printway/ui';

interface PrefsForm {
  theme: Theme;
  language: 'vi' | 'en' | 'fr';
  emailEnabled: boolean;
  notificationChannels: { inApp: boolean; email: boolean; sms: boolean };
}

const INITIAL: PrefsForm = {
  theme: 'system',
  language: 'vi',
  emailEnabled: true,
  notificationChannels: { inApp: true, email: true, sms: false },
};

type LooseClient = {
  GET: (p: string, init?: { params?: unknown }) => Promise<{
    data?: unknown;
    error?: unknown;
    response: Response;
  }>;
  PATCH: (p: string, init?: { body?: unknown }) => Promise<{
    data?: unknown;
    error?: unknown;
    response: Response;
  }>;
};

// dbg-r5 — BE notification-prefs shape (apps/api/src/modules/auth/models/User.ts).
// Categories that can be toggled per channel + a soundEnabled flag. Marketing
// stays off by default. The server force-pins in_app=true for every
// non-marketing category, so the FE inApp toggle here is a
// hint for the marketing category only — included for transparency.
type NotifChannel = 'email' | 'push' | 'in_app';
type NotifCategory =
  | 'orders'
  | 'shipping'
  | 'payment'
  | 'wallet'
  | 'billing'
  | 'system'
  | 'marketing';
type NotifPrefsServer = {
  categories: Record<NotifCategory, Record<NotifChannel, boolean>>;
  soundEnabled: boolean;
};

const ALL_CATEGORIES: NotifCategory[] = [
  'orders', 'shipping', 'payment', 'wallet', 'billing', 'system', 'marketing',
];

function defaultServerPrefs(): NotifPrefsServer {
  const allOn = { email: true, push: true, in_app: true };
  const allOff = { email: false, push: false, in_app: false };
  return {
    categories: {
      orders: { ...allOn },
      shipping: { ...allOn },
      payment: { ...allOn },
      wallet: { ...allOn },
      billing: { ...allOn },
      system: { ...allOn },
      marketing: { ...allOff },
    },
    soundEnabled: true,
  };
}

/** Collapse the dense matrix into the 4-toggle form shape. We probe the
 *  `orders` row as the canonical truth — it is the most-used category and
 *  reflects the user's intent for transactional notifications. */
function matrixToForm(server: NotifPrefsServer): Pick<
  PrefsForm,
  'emailEnabled' | 'notificationChannels'
> {
  const orders = server.categories.orders;
  // emailEnabled is a master kill — true iff any non-marketing category
  // still allows email. Single source of truth keeps the kill-switch
  // semantics intuitive.
  const anyEmail = ALL_CATEGORIES
    .filter((c) => c !== 'marketing')
    .some((c) => server.categories[c].email);
  return {
    emailEnabled: anyEmail,
    notificationChannels: {
      inApp: orders.in_app,
      email: orders.email,
      // FE labels this "sms" historically; we map to the `push` channel
      // because SMS is not a BE channel today.
      sms: orders.push,
    },
  };
}

/** Expand the 4-toggle form back to the dense matrix the BE schema requires.
 *  Non-marketing categories mirror the form; marketing stays off-everywhere
 *  unless the user already had it enabled in the loaded prefs. */
function formToMatrix(
  form: PrefsForm,
  previous: NotifPrefsServer,
): NotifPrefsServer {
  const next: NotifPrefsServer = {
    categories: {} as NotifPrefsServer['categories'],
    soundEnabled: previous.soundEnabled,
  };
  for (const cat of ALL_CATEGORIES) {
    if (cat === 'marketing') {
      // Preserve the user's prior marketing prefs — the simplified UI
      // does not expose marketing toggles, so flipping them silently
      // would be a footgun.
      next.categories.marketing = { ...previous.categories.marketing };
      continue;
    }
    next.categories[cat] = {
      email: form.emailEnabled && form.notificationChannels.email,
      push: form.notificationChannels.sms,
      // BE force-pins in_app=true on non-marketing rows
      // but sending the form's value here keeps wire shape honest.
      in_app: form.notificationChannels.inApp,
    };
  }
  return next;
}

// Map a string from user.language / i18next locale to the form union.
// Defaults to 'vi' when the input doesn't match a supported portal locale.
function toFormLanguage(value: string | null | undefined): PrefsForm['language'] {
  if (!value) return 'vi';
  const lower = value.toLowerCase();
  if (lower === 'en') return 'en';
  if (lower === 'fr') return 'fr';
  return 'vi';
}

export function PreferencesPage(): JSX.Element {
  const user = useAuth((s) => s.user);
  const fetchMe = useAuth((s) => s.fetchMe);
  const { theme, setTheme } = useTheme();
  const { toast } = useToast();
  // Task 79 — i18next bridge. `i18n.language` is the live locale (set by
  // LanguageDetector cookie/localStorage chain or by the TopBar selector).
  // Task 78 batch C8 — bind to 'settings' namespace for label lookups.
  const { i18n, t } = useTranslation('settings');

  const [form, setForm] = useState<PrefsForm>(() => ({
    ...INITIAL,
    theme,
    // Task 79 — prefer the live i18next locale over user.language so the
    // picker reflects the TopBar's current state instead of jumping back
    // to whatever the server last persisted (which may be stale until the
    // next save).
    language: toFormLanguage(i18n.language),
  }));
  // dbg-r5 — keep the last server-side notification matrix so we can
  // round-trip marketing-row prefs that the simplified UI doesn't surface.
  const [serverPrefs, setServerPrefs] = useState<NotifPrefsServer>(() =>
    defaultServerPrefs(),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // dbg-r5 — hydrate notification channels from the BE on mount. Without
  // this the page rendered the INITIAL defaults regardless of what was
  // persisted, so toggling a checkbox + save then reload looked like the
  // save no-op'd (it didn't — it was just never read back).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const client = apiClient() as unknown as LooseClient;
        const res = await client.GET('/api/v1/auth/me/notification-prefs');
        if (cancelled) return;
        if (!res.response.ok) return;
        const env = res.data as
          | { data?: { notificationPrefs?: NotifPrefsServer } }
          | undefined;
        const prefs = env?.data?.notificationPrefs;
        if (!prefs) return;
        setServerPrefs(prefs);
        const flat = matrixToForm(prefs);
        setForm((p) => ({ ...p, ...flat }));
      } catch {
        // Hydration failure is non-fatal — defaults remain.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Hydrate from current user on mount — Task 79: only fall back to
  // `user.language` when i18next hasn't determined a locale yet.
  useEffect(() => {
    if (!user) return;
    setForm((prev) => ({
      ...prev,
      theme: (user.themePreference as Theme) ?? prev.theme,
      language: toFormLanguage(i18n.language ?? user.language),
    }));
  }, [user, i18n.language]);

  // Track theme cycles initiated by TopBar ThemeToggle.
  useEffect(() => {
    setForm((prev) => (prev.theme === theme ? prev : { ...prev, theme }));
  }, [theme]);

  // Task 79 — re-render the form when the TopBar LanguageSelector flips
  // the active locale, so the dropdown stays in sync without a manual save.
  useEffect(() => {
    function onLangChange(lng: string): void {
      setForm((prev) => {
        const next = toFormLanguage(lng);
        return prev.language === next ? prev : { ...prev, language: next };
      });
    }
    i18n.on('languageChanged', onLangChange);
    return () => {
      i18n.off('languageChanged', onLangChange);
    };
  }, [i18n]);

  const handleSubmit = async (
    ev: FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    ev.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      // Apply theme client-side immediately — ThemeProvider also fires its
      // `onThemeChange` (wired in main.tsx) which PATCHes the same endpoint.
      // We still send `themePreference` here so the change persists when the
      // user toggles it from this page directly (no global cycle event).
      setTheme(form.theme);

      // Task 79 — flip i18next BEFORE the PATCH so the UI re-renders
      // immediately on submit, independent of network latency. Fire-and-
      // forget is safe: LanguageDetector persists to cookie+localStorage
      // synchronously, and the BE PATCH below is the source of truth for
      // cross-device sync. We don't await to avoid blocking the network
      // calls on the in-memory locale swap.
      void i18n.changeLanguage(form.language);

      // R6-2 (2026-05-16): /auth/me/preferences zod schema only accepts
      // theme / density / locale / language / timezone. The 4 checkboxes
      // (emailEnabled + notificationChannels.{inApp,email,sms}) live on
      // /auth/me/notification-prefs (a dense category × channel matrix).
      // dbg-r5: we now PATCH both endpoints on save so the checkboxes
      // actually persist instead of silently no-op'ing.
      const body: Record<string, unknown> = {
        theme: form.theme,
        language: form.language,
      };
      const profileBody = { language: form.language };
      const notifBody = formToMatrix(form, serverPrefs);

      const client = apiClient() as unknown as LooseClient;
      const [prefsRes, profileRes, notifRes] = await Promise.all([
        client.PATCH('/api/v1/auth/me/preferences', { body }),
        client.PATCH('/api/v1/auth/me', { body: profileBody }),
        client.PATCH('/api/v1/auth/me/notification-prefs', { body: notifBody }),
      ]);

      if (!prefsRes.response.ok) {
        const env = prefsRes.data as { error?: { code?: string } } | undefined;
        const code = env?.error?.code ?? `HTTP_${prefsRes.response.status}`;
        setError(code);
        toast({
          variant: 'error',
          title: t('preferences.errorTitle'),
          description: code,
        });
        return;
      }
      if (!notifRes.response.ok) {
        const env = notifRes.data as { error?: { code?: string } } | undefined;
        const code = env?.error?.code ?? `HTTP_${notifRes.response.status}`;
        setError(code);
        toast({
          variant: 'error',
          title: t('preferences.errorTitle'),
          description: code,
        });
        return;
      }
      // profile language failure is non-fatal for prefs page — log but don't
      // overwrite a successful prefs save.
      if (!profileRes.response.ok && import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn('[prefs] language PATCH failed', profileRes.response.status);
      }
      // Capture the server response so subsequent saves preserve marketing
      // rows / soundEnabled / any other field the simplified UI doesn't
      // surface but the matrix carries.
      const notifEnv = notifRes.data as
        | { data?: { notificationPrefs?: NotifPrefsServer } }
        | undefined;
      const newServerPrefs = notifEnv?.data?.notificationPrefs;
      if (newServerPrefs) setServerPrefs(newServerPrefs);
      setSuccess(true);
      toast({ variant: 'success', title: t('preferences.success') });
      await fetchMe();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'PREFS_UPDATE_FAILED';
      setError(msg);
      toast({ variant: 'error', title: t('preferences.errorUnknown'), description: msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsLayout portal="vendor" activeTab="preferences">
      <Card>
        <CardHeader>
          <CardTitle>{t('preferences.title')}</CardTitle>
          <CardDescription>
            {t('preferences.subtitle')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form onSubmit={handleSubmit} data-testid="preferences-form">
            <FormField>
              <FormLabel htmlFor="theme">{t('preferences.theme.label')}</FormLabel>
              <FormControl>
                <Select
                  value={form.theme}
                  onValueChange={(value) =>
                    setForm((p) => ({ ...p, theme: value as Theme }))
                  }
                >
                  <SelectTrigger id="theme" data-testid="theme-picker">
                    <SelectValue placeholder={t('preferences.theme.label')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="light">{t('preferences.theme.lightOption')}</SelectItem>
                    <SelectItem value="dark">{t('preferences.theme.darkOption')}</SelectItem>
                    <SelectItem value="system">{t('preferences.theme.systemOption')}</SelectItem>
                  </SelectContent>
                </Select>
              </FormControl>
            </FormField>

            <FormField>
              <FormLabel htmlFor="language">{t('language.label')}</FormLabel>
              <FormControl>
                <Select
                  value={form.language}
                  onValueChange={(value) =>
                    setForm((p) => ({
                      ...p,
                      language: value as PrefsForm['language'],
                    }))
                  }
                >
                  <SelectTrigger id="language" data-testid="language-picker">
                    <SelectValue placeholder={t('language.label')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vi">Tiếng Việt</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="fr">Français</SelectItem>
                  </SelectContent>
                </Select>
              </FormControl>
            </FormField>

            <fieldset className="mt-4 space-y-3 rounded-md border border-pw-border p-4">
              <legend className="px-2 text-sm font-medium text-pw-text">
                {t('preferences.notifications.title')}
              </legend>

              <label className="flex items-center justify-between">
                <span className="text-sm text-pw-text">{t('preferences.notifications.emailEnabled')}</span>
                <input
                  type="checkbox"
                  data-testid="emailEnabled-toggle"
                  checked={form.emailEnabled}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, emailEnabled: e.target.checked }))
                  }
                  className="h-4 w-4 accent-pw-primary-strong"
                />
              </label>

              <label className="flex items-center justify-between">
                <span className="text-sm text-pw-text">{t('preferences.notifications.inApp')}</span>
                <input
                  type="checkbox"
                  data-testid="channel-inApp-toggle"
                  checked={form.notificationChannels.inApp}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      notificationChannels: {
                        ...p.notificationChannels,
                        inApp: e.target.checked,
                      },
                    }))
                  }
                  className="h-4 w-4 accent-pw-primary-strong"
                />
              </label>

              <label className="flex items-center justify-between">
                <span className="text-sm text-pw-text">{t('preferences.notifications.email')}</span>
                <input
                  type="checkbox"
                  data-testid="channel-email-toggle"
                  checked={form.notificationChannels.email}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      notificationChannels: {
                        ...p.notificationChannels,
                        email: e.target.checked,
                      },
                    }))
                  }
                  className="h-4 w-4 accent-pw-primary-strong"
                />
              </label>

              <label className="flex items-center justify-between">
                <span className="text-sm text-pw-text">{t('preferences.notifications.sms')}</span>
                <input
                  type="checkbox"
                  data-testid="channel-sms-toggle"
                  checked={form.notificationChannels.sms}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      notificationChannels: {
                        ...p.notificationChannels,
                        sms: e.target.checked,
                      },
                    }))
                  }
                  className="h-4 w-4 accent-pw-primary-strong"
                />
              </label>
            </fieldset>

            {error ? (
              <FormMessage data-testid="preferences-error">{error}</FormMessage>
            ) : null}
            {success ? (
              <p
                className="text-xs font-medium text-green-600"
                data-testid="preferences-success"
              >
                {t('preferences.success')}
              </p>
            ) : null}

            <div className="flex justify-end pt-2">
              <Button type="submit" disabled={saving} data-testid="preferences-submit">
                {saving ? t('preferences.submitting') : t('preferences.submit')}
              </Button>
            </div>
          </Form>
        </CardContent>
      </Card>
    </SettingsLayout>
  );
}
